import type { PixelConfig } from './types';

const DEFAULTS: PixelConfig = {
  property: 'unknown',
  defaultSuite: 'BRUINS',
  defaultConsent: 'auto',
  beaconEndpoint: null,
  attributionModel: 'last_touch',
  attributionDetail: 'full',
  validityDays: 14,
  maxJourneyLength: 5,
  touchpointCooldownMs: 60 * 60 * 1000, // 1 hour
  urlBudget: 1800,
  debug: false,
  internalDomains: [
    'tdgarden.com',
    'bostonproshop.com',
    'bostongardensociety.com',
    'tdgardenapps.com',
  ],
};

/**
 * Find our own script tag in the DOM and parse data-* attributes.
 * Falls back to the most recently inserted matching tag.
 */
function findOwnScriptTag(): HTMLScriptElement | null {
  if (typeof document === 'undefined') return null;
  // currentScript is reliable during the initial sync execution
  const direct = document.currentScript as HTMLScriptElement | null;
  if (direct && direct.tagName === 'SCRIPT') return direct;

  // Async fallback: find the most recent <script> referencing our path.
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const s = scripts[i] as HTMLScriptElement;
    if (/attribution-pixel|attribution\/v\d+|attribution\/(staging|latest)/.test(s.src)) {
      return s;
    }
  }
  return null;
}

function parseBool(value: string | null | undefined, fallback: boolean): boolean {
  if (value == null) return fallback;
  return /^(true|1|yes|on)$/i.test(value);
}

function parseInt10(value: string | null | undefined, fallback: number): number {
  if (value == null) return fallback;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Build the runtime config from (priority order):
 *   1. window.TDGAttributionConfig (set before the script tag)
 *   2. data-* attributes on the script tag
 *   3. defaults
 */
export function buildConfig(): PixelConfig {
  const script = findOwnScriptTag();
  const ds = script?.dataset ?? {};
  const fromWindow = (typeof window !== 'undefined' && window.TDGAttributionConfig) || {};

  const debugFromUrl =
    typeof window !== 'undefined' &&
    /[?&]tdg_debug=1\b/.test(window.location.search);

  const merged: PixelConfig = {
    property: fromWindow.property ?? ds.property ?? DEFAULTS.property,
    defaultSuite: fromWindow.defaultSuite ?? ds.defaultSuite ?? DEFAULTS.defaultSuite,
    defaultConsent:
      (fromWindow.defaultConsent ?? (ds.defaultConsent as PixelConfig['defaultConsent']) ?? DEFAULTS.defaultConsent),
    beaconEndpoint: fromWindow.beaconEndpoint ?? ds.beaconEndpoint ?? DEFAULTS.beaconEndpoint,
    attributionModel:
      (fromWindow.attributionModel ?? (ds.attributionModel as PixelConfig['attributionModel']) ?? DEFAULTS.attributionModel),
    attributionDetail:
      (fromWindow.attributionDetail ?? (ds.attributionDetail as PixelConfig['attributionDetail']) ?? DEFAULTS.attributionDetail),
    validityDays: fromWindow.validityDays ?? parseInt10(ds.validityDays, DEFAULTS.validityDays),
    maxJourneyLength:
      fromWindow.maxJourneyLength ?? parseInt10(ds.maxJourneyLength, DEFAULTS.maxJourneyLength),
    touchpointCooldownMs:
      fromWindow.touchpointCooldownMs ??
      parseInt10(ds.touchpointCooldownMs, DEFAULTS.touchpointCooldownMs),
    urlBudget: fromWindow.urlBudget ?? parseInt10(ds.urlBudget, DEFAULTS.urlBudget),
    debug: debugFromUrl || (fromWindow.debug ?? parseBool(ds.debug, DEFAULTS.debug)),
    internalDomains: fromWindow.internalDomains ?? DEFAULTS.internalDomains,
  };

  // Validate sensitive values
  if (!['first_touch', 'last_touch', 'linear'].includes(merged.attributionModel)) {
    merged.attributionModel = DEFAULTS.attributionModel;
  }
  if (!['label', 'campaign', 'full'].includes(merged.attributionDetail)) {
    merged.attributionDetail = DEFAULTS.attributionDetail;
  }
  if (!['auto', 'granted', 'denied'].includes(merged.defaultConsent)) {
    merged.defaultConsent = DEFAULTS.defaultConsent;
  }

  return merged;
}
